/**
 * Monitoring API
 * Service health and performance monitoring endpoints
 */

import apiClient from './client';
import type { 
  MonitoringDashboard, 
  CanaryInfo,
  QueueStats,
  DeadLetterQueueResponse,
  RetryResult,
  ClearResult,
  SystemTaskQueueStats,
  SystemTaskDeadLetterResponse,
} from '@/types/monitoring';

export const monitoringApi = {
  /**
   * Get complete monitoring dashboard data
   */
  getDashboard: async (): Promise<MonitoringDashboard> => {
    const response = await apiClient.get('/monitoring/dashboard');
    return response.data;
  },

  /**
   * Get service configuration for dashboard links
   */
  getServicesConfig: async (): Promise<Record<string, { name: string; external_url: string | null }>> => {
    const response = await apiClient.get('/monitoring/services');
    return response.data;
  },

  /**
   * Get canary deployment status
   */
  getCanaryStatus: async (): Promise<CanaryInfo> => {
    const response = await apiClient.get('/monitoring/canary');
    return response.data;
  },

  // ============================================================
  // Dual Extraction Queue APIs
  // ============================================================

  /**
   * Get queue statistics
   */
  getQueueStats: async (): Promise<QueueStats> => {
    const response = await apiClient.get('/monitoring/queue/stats');
    return response.data;
  },

  /**
   * Get dead letter queue tasks
   */
  getDeadLetterQueue: async (limit = 100): Promise<DeadLetterQueueResponse> => {
    const response = await apiClient.get(`/monitoring/queue/dead-letter?limit=${limit}`);
    return response.data;
  },

  /**
   * Retry a single dead letter task
   */
  retryDeadLetterTask: async (taskIndex: number): Promise<RetryResult> => {
    const response = await apiClient.post(`/monitoring/queue/dead-letter/retry/${taskIndex}`);
    return response.data;
  },

  /**
   * Retry all dead letter tasks
   */
  retryAllDeadLetterTasks: async (): Promise<RetryResult> => {
    const response = await apiClient.post('/monitoring/queue/dead-letter/retry-all');
    return response.data;
  },

  /**
   * Clear dead letter queue
   */
  clearDeadLetterQueue: async (): Promise<ClearResult> => {
    const response = await apiClient.delete('/monitoring/queue/dead-letter');
    return response.data;
  },

  /**
   * Reset queue statistics
   */
  resetQueueStats: async (): Promise<ClearResult> => {
    const response = await apiClient.post('/monitoring/queue/reset-stats');
    return response.data;
  },

  // ============================================================
  // System Task Queue APIs
  // ============================================================

  /**
   * Get system task queue statistics
   */
  getSystemTaskQueueStats: async (): Promise<SystemTaskQueueStats> => {
    const response = await apiClient.get('/monitoring/queue/system-task/stats');
    return response.data;
  },

  /**
   * Get system task dead letter queue tasks
   */
  getSystemTaskDeadLetterQueue: async (): Promise<SystemTaskDeadLetterResponse> => {
    const response = await apiClient.get('/monitoring/queue/system-task/dead-letter');
    return response.data;
  },

  /**
   * Retry a single system task from dead letter queue
   */
  retrySystemTaskDeadLetterTask: async (taskIndex: number): Promise<RetryResult> => {
    const response = await apiClient.post(`/monitoring/queue/system-task/dead-letter/retry/${taskIndex}`);
    return response.data;
  },

  /**
   * Retry all system tasks in dead letter queue
   */
  retryAllSystemTaskDeadLetterTasks: async (): Promise<RetryResult> => {
    const response = await apiClient.post('/monitoring/queue/system-task/dead-letter/retry-all');
    return response.data;
  },

  /**
   * Clear system task dead letter queue
   */
  clearSystemTaskDeadLetterQueue: async (): Promise<ClearResult> => {
    const response = await apiClient.delete('/monitoring/queue/system-task/dead-letter');
    return response.data;
  },
};
